<template>
  <aside class="visual-body" id="report-capture">
    <div class="graph-area">
      <div v-if="!chartData || !chartData.length" class="graph-empty">
        선택된 센서 데이터가 없습니다
      </div>

      <div v-else class="graph-list">
        <div class="graph-card">
          <div class="graph-header">
            <div class="graph-header-left">
              <div class="graph-category" v-if="mainChart.groupName || mainChart.subGroupName">
                <span v-if="mainChart.groupName">{{ mainChart.groupName }}</span>
                <span v-if="mainChart.groupName && mainChart.subGroupName" class="slash"> / </span>
                <span v-if="mainChart.subGroupName">{{ mainChart.subGroupName }}</span>
              </div>

              <div class="graph-title">{{ mainChart.sensorName }}</div>
            </div>

            <div class="graph-header-right">
              <div class="axis-control-box">
                <span class="axis-label">축 범위</span>

                <input
                  v-model="axisControlMap.combined.min"
                  type="number"
                  step="1"
                  class="axis-input"
                  placeholder="min"
                  @input="applyChartScale"
                />

                <span class="axis-tilde">~</span>

                <input
                  v-model="axisControlMap.combined.max"
                  type="number"
                  step="1"
                  class="axis-input"
                  placeholder="max"
                  @input="applyChartScale"
                />

                <button class="axis-btn" type="button" @click="resetChartScale">
                  초기화
                </button>
              </div>
            </div>
          </div>

          <div class="graph-inner">
            <canvas ref="combinedCanvas"></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="table-area">

      <div
        v-if="minMaxSummary"
        class="minmax-summary"
      >
        최소값 :
        <strong>{{ formatNumber(minMaxSummary.min) }}</strong>,
        최대값 :
        <strong>{{ formatNumber(minMaxSummary.max) }}</strong>
      </div>

      <div v-if="tableRows.length" class="table-scroll-x">
        <div class="table-scroll-y">
          <table class="data-table">
            <thead>
              <tr>
                <th rowspan="2" class="sticky-col sticky-head">측정일시</th>

                <th
                  v-for="sensor in chartData"
                  :key="sensor.sensorId"
                  :colspan="getVisibleColumnCount(sensor)"
                >
                  <div class="head-sensor-name">{{ sensor.sensorName }}</div>
                  <div v-if="sensor.groupName || sensor.subGroupName" class="head-sensor-sub">
                    {{ [sensor.groupName, sensor.subGroupName].filter(Boolean).join(' / ') }}
                  </div>
                </th>

                <th rowspan="2" class="sticky-head">비고</th>
              </tr>

              <tr>
                <template
                  v-for="sensor in chartData"
                  :key="`${sensor.sensorId}-sub`"
                >

                  <!-- 진동계3축 -->
                  <template v-if="String(sensor.displayType || '').trim().toUpperCase() === 'V3'">
                    <th>X축(kine)</th>
                    <th>Y축(kine)</th>
                    <th>Z축(kine)</th>
                    <th>PVS(kine)</th>
                  </template>

                  <!-- 하중계 -->
                  <template v-else-if="String(sensor.displayType || '').trim().toUpperCase() === 'LC'">
                    <th>1축</th>
                    <th>2축</th>
                    <th>3축</th>
                    <th>하중(Ton)</th>
                    <th>전회대비(Ton)</th>
                    <th>초기대비(Ton)</th>
                  </template>

                  <!-- 기존 센서 -->
                  <template v-else>
                    <th v-if="sensor.showAmount">
                      {{ sensor.amountLabel || '측정치' }}
                    </th>

                    <th v-if="sensor.showDeg">
                      {{ sensor.degLabel || '변화량(deg)' }}
                    </th>

                    <th v-if="sensor.showMm">
                      {{ sensor.mmLabel || '변위량(mm)' }}
                    </th>

                    <th v-if="sensor.showDiff">
                      {{ getPreviousDiffLabel(sensor) }}
                    </th>

                    <th v-if="sensor.showDiff">
                      {{ getInitialDiffLabel(sensor) }}
                    </th>

                    <th v-if="sensor.showAngular">
                      {{ sensor.angularLabel || '각변위' }}
                    </th>
                  </template>

                </template>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(row, rowIdx) in tableRows"
                :key="row.rowKey || rowIdx"
                :class="{ 'initial-row': row.remark === '초기치' }"
              >
                <td class="sticky-col">{{ formatShortYearDate(row.time) }}</td>

              <template
                v-for="sensor in chartData"
                :key="`${sensor.sensorId}-row-${rowIdx}`"
              >

                <!-- 진동계3축 -->
                <template v-if="String(sensor.displayType || '').trim().toUpperCase() === 'V3'">
                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_x`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_y`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_z`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_pvs`]) }}
                  </td>
                </template>

                <!-- 하중계 -->
                <template v-else-if="String(sensor.displayType || '').trim().toUpperCase() === 'LC'">

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_axis1`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_axis2`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_axis3`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_amount`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_previousDiff`]) }}
                  </td>

                  <td>
                    {{ formatNumber(row[`${sensor.sensorId}_initialDiff`]) }}
                  </td>

                </template>

                <!-- 기존 센서 -->
                <template v-else>
                  <td v-if="sensor.showAmount">
                    {{ formatNumber(row[`${sensor.sensorId}_amount`]) }}
                  </td>

                  <td v-if="sensor.showDeg">
                    {{ formatNumber(row[`${sensor.sensorId}_deg`]) }}
                  </td>

                  <td v-if="sensor.showMm">
                    {{ formatNumber(row[`${sensor.sensorId}_mm`]) }}
                  </td>

                  <td v-if="sensor.showDiff">
                    {{ formatNumber(row[`${sensor.sensorId}_previousDiff`]) }}
                  </td>

                  <td v-if="sensor.showDiff">
                    {{ formatNumber(row[`${sensor.sensorId}_initialDiff`]) }}
                  </td>

                  <td v-if="sensor.showAngular">
                    {{ formatAngular(row[`${sensor.sensorId}_angular`]) }}
                  </td>
                </template>

              </template>

                <td>{{ row.remark || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="table-empty">
        표시할 데이터가 없습니다
      </div>
    </div>
  </aside>
</template>

<script>
import { markRaw } from 'vue'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

const COLOR_LIST = ['#5aa9ff', '#b27a42', '#60a5fa', '#10b981']

const COLOR_MAP = {
  level1: '#65c8b8',
  level2: '#f0b53a',
  level3: '#f36a6a'
}

export default {
  name: 'ChartArea',

  props: {
    chartData: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      charts: {},
      axisControlMap: {
        combined: {
          min: '',
          max: '',
          originalMin: '',
          originalMax: ''
        }
      }
    }
  },

  computed: {
    mainChart() {
      return this.chartData?.[0] || {}
    },

    chartSignature() {
      if (!Array.isArray(this.chartData) || !this.chartData.length) return ''

      return this.chartData.map(sensor => {
        return [
          sensor.sensorId,
          sensor.sensorName || '',
          sensor.chartValueKey || 'mm',
          sensor.chartMin ?? '',
          sensor.chartMax ?? '',
          JSON.stringify(sensor.labels ?? []),
          JSON.stringify(sensor.values ?? []),
          sensor.showAmount ? 1 : 0,
          sensor.showDeg ? 1 : 0,
          sensor.showMm ? 1 : 0,
          sensor.showDiff ? 1 : 0,
          sensor.showAngular ? 1 : 0,
          sensor.baseMeasuredAt || '',
          JSON.stringify(sensor.basePoint ?? {}),
          JSON.stringify(sensor.thresholdLines ?? [])
        ].join('|')
      }).join('||')
    },

    tableRows() {
      if (!this.chartData || !this.chartData.length) return []

      const labels = this.mainChart.labels ?? []

      const rowsAsc = labels.map((time, idx) => {
        const row = {
          rowKey: `data-${idx}`,
          time,
          remark: ''
        }

        this.chartData.forEach(sensor => {
          const val = sensor.values?.[idx] ?? {}

          row[`${sensor.sensorId}_amount`] = val.amount
          row[`${sensor.sensorId}_deg`] = val.deg
          row[`${sensor.sensorId}_mm`] = val.mm

          row[`${sensor.sensorId}_x`] = val.x
          row[`${sensor.sensorId}_y`] = val.y
          row[`${sensor.sensorId}_z`] = val.z
          row[`${sensor.sensorId}_pvs`] = val.pvs

          row[`${sensor.sensorId}_previousDiff`] = val.previousDiff
          row[`${sensor.sensorId}_initialDiff`] = val.initialDiff
          row[`${sensor.sensorId}_angular`] = this.calculateAngular(val)

          row[`${sensor.sensorId}_axis1`] = val.axis1
          row[`${sensor.sensorId}_axis2`] = val.axis2
          row[`${sensor.sensorId}_axis3`] = val.axis3
        })

        return row
      })

      const rowsDesc = [...rowsAsc].reverse()

      if (this.mainChart?.basePoint) {
        const initialRow = {
          rowKey: 'initial-row',
          time: this.mainChart.baseMeasuredAt || '',
          remark: '초기치'
        }

        this.chartData.forEach(sensor => {
          const basePoint = sensor.basePoint ?? {}

          initialRow[`${sensor.sensorId}_amount`] = basePoint.amount
          initialRow[`${sensor.sensorId}_x`] = basePoint.x ?? 0
          initialRow[`${sensor.sensorId}_y`] = basePoint.y ?? 0
          initialRow[`${sensor.sensorId}_z`] = basePoint.z ?? 0
          initialRow[`${sensor.sensorId}_pvs`] = basePoint.pvs ?? basePoint.amount ?? 0
          initialRow[`${sensor.sensorId}_deg`] = 0
          initialRow[`${sensor.sensorId}_mm`] = 0
          initialRow[`${sensor.sensorId}_previousDiff`] = 0
          initialRow[`${sensor.sensorId}_initialDiff`] = 0
          initialRow[`${sensor.sensorId}_angular`] = '1/0'
          initialRow[`${sensor.sensorId}_axis1`] = basePoint.axis1 ?? 0
          initialRow[`${sensor.sensorId}_axis2`] = basePoint.axis2 ?? 0
          initialRow[`${sensor.sensorId}_axis3`] = basePoint.axis3 ?? 0
        })

        return [initialRow, ...rowsDesc]
      }

      return rowsDesc
    },

    minMaxSummary() {
      if (!this.chartData?.length || !this.tableRows?.length) {
        return null
      }

      const mainSensor = this.mainChart
      const displayType = String(mainSensor.displayType || '')
        .trim()
        .toUpperCase()

      const targetKeys = []

      if (displayType === 'WM') {
        targetKeys.push(`${mainSensor.sensorId}_initialDiff`)

      } else if (displayType === 'CM') {
        targetKeys.push(`${mainSensor.sensorId}_initialDiff`)

      } else if (displayType === 'FM') {
        targetKeys.push(`${mainSensor.sensorId}_initialDiff`)

      } else if (displayType === 'V') {
        // 진동계: 측정값(PVS) 기준
        targetKeys.push(`${mainSensor.sensorId}_amount`)

      } else if (displayType === 'V3') {
        // 진동계3축: PVS(kine) 기준
        targetKeys.push(`${mainSensor.sensorId}_pvs`)

      } else if (displayType === 'LC') {
        // 하중계: 초기대비(Ton) 기준
        targetKeys.push(`${mainSensor.sensorId}_initialDiff`)

      } else if (displayType === 'GA') {
        // 지표침하계: 초기대비(mm) 기준
        targetKeys.push(`${mainSensor.sensorId}_initialDiff`)

      } else if (displayType === 'R') {
        // 단일센서 레일변위계: 누적 처짐량(mm) 기준
        targetKeys.push(`${mainSensor.sensorId}_mm`)

      } else if (displayType === 'IA') {
        // 구조물경사계: X/Y 변위량(mm) 전체 기준
        this.chartData.forEach(sensor => {
          targetKeys.push(`${sensor.sensorId}_mm`)
        })
      }

      if (!targetKeys.length) {
        return null
      }

      const values = this.tableRows
        .filter(row => row.remark !== '초기치')
        .flatMap(row =>
          targetKeys.map(key => Number(row[key]))
        )
        .filter(v => Number.isFinite(v))

      if (!values.length) {
        return null
      }

      return {
        min: Math.min(...values),
        max: Math.max(...values)
      }
    },
  },

  watch: {
    chartSignature: {
      immediate: true,
      handler() {
        this.syncAxisControls()

        this.$nextTick(() => {
          this.renderCombinedChart()
        })
      }
    }
  },

  beforeUnmount() {
    this.destroyAllCharts()
  },

  methods: {
    formatShortYearDate(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return String(value).replace(/^(\d{2})\d{2}-/, '$1-')
      }

      const yy = String(date.getFullYear()).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },

    syncAxisControls() {
      const prev = this.axisControlMap.combined || {}

      const nextOriginalMin = this.toInputValue(this.mainChart.chartMin)
      const nextOriginalMax = this.toInputValue(this.mainChart.chartMax)

      const originalChanged =
        prev.originalMin !== nextOriginalMin ||
        prev.originalMax !== nextOriginalMax

      this.axisControlMap = {
        combined: {
          min: originalChanged ? nextOriginalMin : (prev.min || nextOriginalMin),
          max: originalChanged ? nextOriginalMax : (prev.max || nextOriginalMax),
          originalMin: nextOriginalMin,
          originalMax: nextOriginalMax
        }
      }
    },

    toInputValue(value) {
      const num = Number(value)
      return Number.isFinite(num) ? String(num) : ''
    },

    parseAxisValue(value) {
      if (value === null || value === undefined) return undefined

      const trimmed = String(value).trim()
      if (!trimmed) return undefined

      const num = Number(trimmed)
      return Number.isFinite(num) ? num : undefined
    },

    getVisibleColumnCount(sensor) {
      const displayType = String(sensor.displayType || '').trim().toUpperCase()

      if (displayType === 'V3') {
        return 4
      }

      if (displayType === 'LC') {
        return 6
      }

      let count = 0

      if (sensor.showAmount) count++
      if (sensor.showDeg) count++
      if (sensor.showMm) count++
      if (sensor.showDiff) count += 2
      if (sensor.showAngular) count++

      return count || 1
    },

    destroyAllCharts() {
      for (const ch of Object.values(this.charts)) {
        if (ch && typeof ch.destroy === 'function') {
          ch.destroy()
        }
      }

      this.charts = {}
    },

    formatNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return num.toFixed(4)
    },

    calculateAngular(value) {
      if (value?.angular !== undefined && value?.angular !== null) {
        return value.angular
      }

      const deg = Number(Number(value?.deg).toFixed(4))
      if (!Number.isFinite(deg) || deg === 0) return '1/0'

      const rad = Math.abs(deg * 0.0175)
      if (!Number.isFinite(rad) || rad === 0) return '1/0'

      return Math.round(1 / rad)
    },

    formatAngular(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (String(value).startsWith('1/')) return value

      const num = Number(value)
      if (!Number.isFinite(num)) return '-'

      return `1/${Math.round(num)}`
    },

    getChartSeries(sensor) {
      if (!sensor || !sensor.values) {
        return []
      }

      // 진동계(3축): 그래프는 PVS(kine)만 그림
      if (String(sensor.displayType || '').trim().toUpperCase() === 'V3') {
        return sensor.values.map(v => {
          if (!v) return null

          const pvs = v.pvs ?? v.amount

          if (pvs === null || pvs === undefined || pvs === '') {
            return null
          }

          return Number(pvs)
        })
      }

      const key = sensor.chartValueKey || 'amount'

      return sensor.values.map(v => {
        if (!v) return null

        if (key === 'amount') return v.amount
        if (key === 'deg') return v.deg
        if (key === 'mm') return v.mm

        return v[key]
      })
    },

    buildSensorDatasets() {
      return this.chartData.map((sensor, index) => ({
        label: sensor.sensorName,
        data: this.getChartSeries(sensor),
        borderColor: COLOR_LIST[index % COLOR_LIST.length],
        backgroundColor: COLOR_LIST[index % COLOR_LIST.length],
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 3,
        tension: 0,
        fill: false,
        customType: 'sensor'
      }))
    },

    buildThresholdDatasets() {
      const labels = this.mainChart.labels ?? []
      const thresholdLines =
        this.mainChart.thresholdLines ?? []

      const levelMap = {}

      thresholdLines.forEach(line => {
        const key = line.colorKey || line.level || line.label
        if (!levelMap[key]) {
          levelMap[key] = []
        }
        levelMap[key].push(line)
      })

      const datasets = []

      Object.entries(levelMap).forEach(([key, lines]) => {
        const sorted = [...lines].sort((a, b) => Number(b.value) - Number(a.value))
        const values = sorted.map(item => item.value)
        const color = COLOR_MAP[key] || COLOR_MAP[sorted[0]?.colorKey] || '#999999'

        sorted.forEach((line, idx) => {
          const thresholdLabel = this.resolveThresholdLabel(key, line.label)

          datasets.push({
            label: idx === 0
              ? `${thresholdLabel}(${values.join(', ')})`
              : `${thresholdLabel} hidden`,
            data: Array(labels.length).fill(line.value),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
            fill: false,
            customType: 'threshold',
            thresholdGroup: key,
            hiddenLegend: idx !== 0
          })
        })
      })

      return datasets
    },

    resolveThresholdLabel(key, fallback) {
      const text = String(fallback || '')

      if (text.includes('1차')) return '1차관리기준치'
      if (text.includes('2차')) return '2차관리기준치'
      if (text.includes('3차')) return '3차관리기준치'

      if (key === 'level1') return '1차관리기준치'
      if (key === 'level2') return '2차관리기준치'
      if (key === 'level3') return '3차관리기준치'

      return text.replace(/\([^)]*\)/g, '') || '관리기준치'
    },

    buildYAxisOptions() {
      const axisControl = this.axisControlMap.combined || {}

      return {
        min: this.parseAxisValue(axisControl.min),
        max: this.parseAxisValue(axisControl.max)
      }
    },

    applyChartScale() {
      const chart = this.charts.combined
      if (!chart) return

      const yAxisOptions = this.buildYAxisOptions()

      if (yAxisOptions.min === undefined) {
        delete chart.options.scales.y.min
      } else {
        chart.options.scales.y.min = yAxisOptions.min
      }

      if (yAxisOptions.max === undefined) {
        delete chart.options.scales.y.max
      } else {
        chart.options.scales.y.max = yAxisOptions.max
      }

      chart.update('none')
    },

    resetChartScale() {
      const axisControl = this.axisControlMap.combined
      if (!axisControl) return

      axisControl.min = axisControl.originalMin
      axisControl.max = axisControl.originalMax

      this.applyChartScale()
    },

    renderCombinedChart() {
      if (!this.chartData || !this.chartData.length) {
        this.destroyAllCharts()
        return
      }

      const canvas = this.$refs.combinedCanvas
      if (!canvas) return

      const existingChart = this.charts.combined
      if (existingChart) {
        existingChart.destroy()
        delete this.charts.combined
      }

      const datasets = [
        ...this.buildSensorDatasets(),
        ...this.buildThresholdDatasets()
      ]

      const yAxisOptions = this.buildYAxisOptions()

      const chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: this.mainChart.labels ?? [],
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                filter(item, chartData) {
                  const dataset = chartData.datasets[item.datasetIndex]
                  return !dataset?.hiddenLegend
                }
              },
              onClick(e, legendItem, legend) {
                const chart = legend.chart
                const clickedDataset = chart.data.datasets[legendItem.datasetIndex]

                if (!clickedDataset) return

                // 관리기준치: +값/-값 한 묶음으로 토글
                if (clickedDataset.customType === 'threshold') {
                  const groupKey = clickedDataset.thresholdGroup

                  const groupIndexes = chart.data.datasets
                    .map((dataset, index) => ({
                      dataset,
                      index
                    }))
                    .filter(item =>
                      item.dataset.customType === 'threshold' &&
                      item.dataset.thresholdGroup === groupKey
                    )
                    .map(item => item.index)

                  const shouldHide = groupIndexes.some(index =>
                    chart.isDatasetVisible(index)
                  )

                  groupIndexes.forEach(index => {
                    chart.setDatasetVisibility(index, !shouldHide)
                  })

                  chart.update()
                  return
                }

                // 일반 센서 데이터는 기존처럼 개별 토글
                const index = legendItem.datasetIndex
                chart.setDatasetVisibility(index, !chart.isDatasetVisible(index))
                chart.update()
              }
            },
            tooltip: {
              filter(context) {
                return context.dataset.customType !== 'threshold'
              },
              callbacks: {
                title: (tooltipItems) => {
                  const raw = tooltipItems?.[0]?.label
                  return this.formatShortYearDate(raw)
                },

                label(context) {
                  const label = context.dataset.label || ''
                  const value = context.parsed.y
                  return `${label}: ${Number(value).toFixed(4)}`
                }
              }
            }
          },
          scales: {
            x: {
              ticks: {
                maxRotation: 45,
                minRotation: 0,

                callback: (value) => {
                  const label = this.mainChart.labels?.[value]
                  return this.formatShortYearDate(label)
                }
              }
            },
            y: {
              min: yAxisOptions.min,
              max: yAxisOptions.max
            }
          }
        }
      })

      this.charts.combined = markRaw(chart)
    },

    getPreviousDiffLabel(sensor) {
      return sensor?.previousDiffLabel || '전회대비(mm)'
    },

    getInitialDiffLabel(sensor) {
      return sensor?.initialDiffLabel || '초기대비(mm)'
    },
  }
}
</script>

<style scoped>
.visual-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.graph-area,
.table-area {
  min-height: 0;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  flex-shrink: 0;
}

.graph-area {
  height: auto;
}

.table-area {
  padding-bottom: 8px;
}

.graph-empty,
.table-empty {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
}

.graph-list {
  padding: 12px;
  box-sizing: border-box;
}

.graph-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.graph-header {
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.graph-header-left {
  min-width: 0;
}

.graph-category {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
}

.graph-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.graph-header-right {
  flex-shrink: 0;
}

.axis-control-box {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.axis-label,
.axis-tilde {
  font-size: 12px;
  color: #4b5563;
}

.axis-input {
  width: 74px;
  height: 30px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 12px;
  box-sizing: border-box;
}

.axis-btn {
  height: 30px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;

  transition: all .2s ease;
}

.axis-btn:hover {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
  box-shadow: 0 2px 8px rgba(37,99,235,.25);
}

.axis-btn:active {
  transform: translateY(1px);
}

.graph-inner {
  height: 360px;
  min-height: 360px;
}

.graph-inner canvas {
  width: 100% !important;
  height: 100% !important;
}

.minmax-summary {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 16px 0;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.minmax-summary strong {
  margin: 0 4px;
  font-weight: 700;
  color: #111827;
}

/* =========================
   TABLE
========================= */

.table-scroll-x {
  width: 100%;
  overflow-x: auto;
}

.table-scroll-y {
  width: 100%;
  max-height: none;
  overflow-y: visible;
}

/*
  컬럼 폭을 특정 컬럼만 고정하지 않고,
  전체 테이블 폭 기준으로 모든 컬럼이 같이 반응하게 한다.
*/
.data-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 12px;
  text-align: center;
  table-layout: fixed;
}

.data-table th,
.data-table td {
  border: 1px solid #d1d5db;
  padding: 7px 8px;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
  line-height: 1.35;
  vertical-align: middle;
}

.data-table thead th {
  background: #f3f4f6;
  font-weight: 700;
  color: #111827;
  position: static;
  top: auto;
  z-index: auto;
}

/* 측정일시도 고정폭 제거 */
.data-table th:first-child,
.data-table td:first-child {
  width: auto;
  min-width: 0;
}

/* 비고 컬럼도 고정폭 제거 */
.data-table th:last-child,
.data-table td:last-child {
  width: auto;
  min-width: 0;
}

/* 기존 nth-child 고정폭 제거 */
.data-table th:nth-child(5),
.data-table td:nth-child(5),
.data-table th:nth-child(9),
.data-table td:nth-child(9) {
  width: auto;
  min-width: 0;
}

.data-table thead tr:nth-child(2) th {
  top: auto;
  z-index: auto;
}

.sticky-col {
  position: static;
  left: auto;
  background: inherit;
  z-index: auto;
}

.sticky-head {
  z-index: 3 !important;
}

.data-table thead .sticky-col {
  background: #f3f4f6;
  z-index: 3;
}

.head-sensor-name {
  font-weight: 700;
}

.head-sensor-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 400;
}

.initial-row td {
  background: #fff7ed;
  font-weight: 600;
}

@media (max-width: 1100px) {
  .graph-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .axis-control-box {
    justify-content: flex-start;
  }

  .graph-inner {
    height: 320px;
    min-height: 320px;
  }

  .data-table {
    min-width: 680px;
    font-size: 11px;
  }

  .data-table th,
  .data-table td {
    padding: 6px 6px;
  }
}
</style>